import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Pencil, ChevronUp, ChevronDown, CheckCircle2, ListFilter } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  AdminRequirement,
  AdminQuestion,
  getMissionRequirements,
  createMissionRequirement,
  updateMissionRequirement,
  deleteMissionRequirement,
  getMissionQuestions,
  createMissionQuestion,
  updateMissionQuestion,
  deleteMissionQuestion,
} from '@/api/admin';
import { useToast } from '@/components/ui/Toast';

type QuestionType = AdminQuestion['question_type'];

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'SINGLE_CHOICE', label: 'اختيار واحد' },
  { value: 'MULTIPLE_CHOICE', label: 'اختيارات متعددة' },
  { value: 'YES_NO', label: 'نعم / لا' },
  { value: 'TEXT', label: 'كتابة نصية' },
];

interface RequirementDraft {
  localId: string; // temp identifier for new (unsaved) items
  id?: string;
  text: string;
  requires_acceptance: boolean;
  saveError?: string;
}

interface QuestionDraft {
  localId: string;
  id?: string;
  question_text: string;
  question_type: QuestionType;
  required: boolean;
  options: string[];
  saveError?: string;
}

interface Props {
  missionId: string; // existing mission id when editing (persist to backend)
  mode: 'create' | 'edit';
  // Create mode: builder holds local drafts, parent collects on submit.
  requirements: RequirementDraft[];
  setRequirements: (d: RequirementDraft[]) => void;
  questions: QuestionDraft[];
  setQuestions: (d: QuestionDraft[]) => void;
  // Edit mode: when provided, load + persist from backend. Ownership:
  onRequirementChange?: (req: RequirementDraft) => void;
}

function parseOptions(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((o) => typeof o === 'string') : [];
  } catch {
    return [];
  }
}

export default function MissionRequirementsQuestionsBuilder({
  missionId,
  mode,
  requirements,
  setRequirements,
  questions,
  setQuestions,
}: Props) {
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(mode === 'edit');
  const [loaded, setLoaded] = useState(mode !== 'edit');
  const [savingReq, setSavingReq] = useState<string | null>(null);
  const [savingNewReq, setSavingNewReq] = useState(false);
  const [savingQ, setSavingQ] = useState<string | null>(null);
  const [savingNewQ, setSavingNewQ] = useState(false);

  // ---- Load existing (edit mode) ----
  const load = useCallback(async () => {
    if (mode !== 'edit' || loaded || loading) return;
    setLoading(true);
    try {
      const [reqs, qs] = await Promise.all([
        getMissionRequirements(missionId),
        getMissionQuestions(missionId),
      ]);
      setRequirements(reqs.map((r: AdminRequirement) => ({
        localId: r.id,
        id: r.id,
        text: r.text,
        requires_acceptance: Number(r.requires_acceptance) === 1,
      })));
      setQuestions(qs.map((q: AdminQuestion) => ({
        localId: q.id,
        id: q.id,
        question_text: q.question_text,
        question_type: q.question_type,
        required: Number(q.required) === 1,
        options: parseOptions(q.options),
      })));
      setLoaded(true);
    } catch (err: any) {
      toastError(err.message || 'فشل تحميل الشروط والأسئلة');
    } finally {
      setLoading(false);
    }
  }, [missionId, mode, loaded, loading, setRequirements, setQuestions, toastError]);

  useEffect(() => { load(); }, [load]);

  // ---- Requirements helpers ----
  const addRequirement = () => {
    setRequirements([...requirements, {
      localId: `new-${Date.now()}`,
      text: '',
      requires_acceptance: true,
    }]);
  };

  const updateReqLocal = (localId: string, patch: Partial<RequirementDraft>) => {
    setRequirements(requirements.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
  };

  const removeRequirement = async (r: RequirementDraft) => {
    if (r.id && mode === 'edit') {
      try {
        await deleteMissionRequirement(missionId, r.id);
        toastSuccess('تم حذف الشرط');
      } catch (err: any) {
        toastError(err.message || 'فشل حذف الشرط');
        return;
      }
    }
    setRequirements(requirements.filter((x) => x.localId !== r.localId));
  };

  const persistRequirement = async (r: RequirementDraft, index: number) => {
    if (!r.text.trim()) {
      toastError('اكتب نص الشرط أولاً');
      return;
    }
    const sort_order = index;
    try {
      if (r.id) {
        await updateMissionRequirement(missionId, r.id, {
          text: r.text.trim(),
          requires_acceptance: r.requires_acceptance,
          sort_order,
        });
        toastSuccess('تم تحديث الشرط');
      } else {
        const saved = await createMissionRequirement(missionId, {
          text: r.text.trim(),
          requires_acceptance: r.requires_acceptance,
          sort_order,
        });
        updateReqLocal(r.localId, { id: saved.id });
        toastSuccess('تمت إضافة الشرط');
      }
    } catch (err: any) {
      toastError(err.message || 'فشل حفظ الشرط');
    }
  };

  const addQuestion = () => {
    setQuestions([...questions, {
      localId: `new-${Date.now()}-${questions.length}`,
      question_text: '',
      question_type: 'TEXT',
      required: true,
      options: [],
    }]);
  };

  const updateQLocal = (localId: string, patch: Partial<QuestionDraft>) => {
    setQuestions(questions.map((q) => (q.localId === localId ? { ...q, ...patch } : q)));
  };

  const removeQuestion = async (q: QuestionDraft) => {
    if (q.id && mode === 'edit') {
      try {
        await deleteMissionQuestion(missionId, q.id);
        toastSuccess('تم حذف السؤال');
      } catch (err: any) {
        toastError(err.message || 'فشل حذف السؤال');
        return;
      }
    }
    setQuestions(questions.filter((x) => x.localId !== q.localId));
  };

  const setQuestionType = (q: QuestionDraft) => (type: QuestionType) => {
    if (type === 'YES_NO') {
      updateQLocal(q.localId, { question_type: type, options: ['نعم', 'لا'] });
    } else if (type !== 'SINGLE_CHOICE' && type !== 'MULTIPLE_CHOICE') {
      updateQLocal(q.localId, { question_type: type, options: [] });
    } else {
      updateQLocal(q.localId, { question_type: type });
    }
  };

  const updateOption = (q: QuestionDraft, optIndex: number, value: string) => {
    const options = q.options.map((o, i) => (i === optIndex ? value : o));
    updateQLocal(q.localId, { options });
  };

  const addOption = (q: QuestionDraft) => {
    updateQLocal(q.localId, { options: [...q.options, ''] });
  };

  const removeOption = (q: QuestionDraft, optIndex: number) => {
    updateQLocal(q.localId, { options: q.options.filter((_, i) => i !== optIndex) });
  };

  const persistQuestion = async (q: QuestionDraft, index: number) => {
    if (!q.question_text.trim()) {
      toastError('اكتب نص السؤال أولاً');
      return;
    }
    if ((q.question_type === 'SINGLE_CHOICE' || q.question_type === 'MULTIPLE_CHOICE')
        && q.options.filter((o) => o.trim()).length < 2) {
      toastError('أضف خيارين صالحين على الأقل للاختيار');
      return;
    }
    const sort_order = index;
    try {
      if (q.id) {
        await updateMissionQuestion(missionId, q.id, {
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          required: q.required,
          options: q.question_type === 'SINGLE_CHOICE' || q.question_type === 'MULTIPLE_CHOICE'
            ? q.options.filter((o) => o.trim())
            : [],
          sort_order,
        });
        toastSuccess('تم تحديث السؤال');
      } else {
        const saved = await createMissionQuestion(missionId, {
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          required: q.required,
          options: q.question_type === 'SINGLE_CHOICE' || q.question_type === 'MULTIPLE_CHOICE'
            ? q.options.filter((o) => o.trim())
            : [],
          sort_order,
        });
        updateQLocal(q.localId, { id: saved.id });
        toastSuccess('تمت إضافة السؤال');
      }
    } catch (err: any) {
      toastError(err.message || 'فشل حفظ السؤال');
    }
  };

  const moveRequirement = (index: number, dir: -1 | 1) => {
    const next = [...requirements];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setRequirements(next);
  };

  const moveQuestion = (index: number, dir: -1 | 1) => {
    const next = [...questions];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setQuestions(next);
  };

  if (loading) {
    return <p className="text-sm text-slate-500 text-center py-4">جاري تحميل الشروط والأسئلة…</p>;
  }

  return (
    <div className="space-y-6">
      {/* ============ REQUIREMENTS ============ */}
      <div className="border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="bg-brand-50 text-brand-700 h-7 w-7 rounded-lg flex items-center justify-center">
              <ListFilter className="h-4 w-4" />
            </span>
            <h4 className="font-bold text-slate-900 text-sm">⚙️ شروط المهمة</h4>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addRequirement}>
            <Plus className="h-3.5 w-3.5" /> إضافة شرط
          </Button>
        </div>

        {requirements.length === 0 ? (
          <p className="text-xs text-slate-400 py-2">بدون شروط</p>
        ) : (
          <div className="space-y-3">
            {requirements.map((r, index) => (
              <div key={r.localId} className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/50">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-slate-400">الشرط {index + 1}</span>
                  <div className="flex items-center gap-1">
                    <IconButton label="تحريك لأعلى" type="button" variant="ghost" size="sm"
                      onClick={() => moveRequirement(index, -1)} disabled={index === 0}>
                      <ChevronUp className="h-4 w-4" />
                    </IconButton>
                    <IconButton label="تحريك لأسفل" type="button" variant="ghost" size="sm"
                      onClick={() => moveRequirement(index, 1)} disabled={index === requirements.length - 1}>
                      <ChevronDown className="h-4 w-4" />
                    </IconButton>
                    <IconButton label="حذف الشرط" type="button" variant="ghost" size="sm" onClick={() => removeRequirement(r)}>
                      <Trash2 className="h-4 w-4 text-danger-600" />
                    </IconButton>
                  </div>
                </div>
                <textarea
                  value={r.text}
                  onChange={(e) => updateReqLocal(r.localId, { text: e.target.value })}
                  placeholder="مثال: المهمة مخصصة للمتطوعين من الكادر الطبي فقط."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`req-accept-${r.localId}`}
                    checked={r.requires_acceptance}
                    onChange={(e) => updateReqLocal(r.localId, { requires_acceptance: e.target.checked })}
                    className="w-4 h-4 text-brand-600 border-slate-300 rounded"
                  />
                  <label htmlFor={`req-accept-${r.localId}`} className="text-xs font-semibold text-slate-700 cursor-pointer">
                    ☑️ يجب على المتطوع الموافقة على الشرط
                  </label>
                </div>
                {r.saveError && <p className="text-xs text-danger-600 font-medium">{r.saveError}</p>}
                {mode === 'edit' && (
                  <Button type="button" variant="secondary" size="sm"
                    loading={savingReq === r.localId}
                    onClick={() => { setSavingReq(r.localId); persistRequirement(r, index).finally(() => setSavingReq(null)); }}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> حفظ الشرط
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============ QUESTIONS ============ */}
      <div className="border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="bg-brand-50 text-brand-700 h-7 w-7 rounded-lg flex items-center justify-center">
              <ListFilter className="h-4 w-4" />
            </span>
            <h4 className="font-bold text-slate-900 text-sm">❓ أسئلة التسجيل</h4>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
            <Plus className="h-3.5 w-3.5" /> إضافة سؤال
          </Button>
        </div>

        {questions.length === 0 ? (
          <p className="text-xs text-slate-400 py-2">لا توجد أسئلة</p>
        ) : (
          <div className="space-y-3">
            {questions.map((q, index) => (
              <div key={q.localId} className="border border-slate-200 rounded-xl p-3 space-y-3 bg-slate-50/50">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-slate-400">السؤال {index + 1}</span>
                  <div className="flex items-center gap-1">
                    <IconButton label="تحريك لأعلى" type="button" variant="ghost" size="sm"
                      onClick={() => moveQuestion(index, -1)} disabled={index === 0}>
                      <ChevronUp className="h-4 w-4" />
                    </IconButton>
                    <IconButton label="تحريك لأسفل" type="button" variant="ghost" size="sm"
                      onClick={() => moveQuestion(index, 1)} disabled={index === questions.length - 1}>
                      <ChevronDown className="h-4 w-4" />
                    </IconButton>
                    <IconButton label="حذف السؤال" type="button" variant="ghost" size="sm" onClick={() => removeQuestion(q)}>
                      <Trash2 className="h-4 w-4 text-danger-600" />
                    </IconButton>
                  </div>
                </div>

                <Input
                  label="نص السؤال"
                  value={q.question_text}
                  onChange={(e) => updateQLocal(q.localId, { question_text: e.target.value })}
                  placeholder="مثال: ما تخصصك؟"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Select
                    label="نوع الإجابة"
                    value={q.question_type}
                    onChange={(e) => setQuestionType(q)(e.target.value as QuestionType)}
                  >
                    {QUESTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Select>

                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={q.required}
                        onChange={(e) => updateQLocal(q.localId, { required: e.target.checked })}
                        className="w-4 h-4 text-brand-600 border-slate-300 rounded"
                      />
                      <span className="text-sm font-semibold text-slate-700">☑️ مطلوب</span>
                    </label>
                  </div>
                </div>

                {(q.question_type === 'SINGLE_CHOICE' || q.question_type === 'MULTIPLE_CHOICE') && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-600">
                        {q.question_type === 'SINGLE_CHOICE' ? 'الاختيارات (اختيار واحد)' : 'الاختيارات (اختيارات متعددة)'}
                      </span>
                      <button
                        type="button"
                        onClick={() => addOption(q)}
                        className="text-xs font-bold text-brand-600 hover:underline flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" /> إضافة اختيار
                      </button>
                    </div>
                    {q.options.map((opt, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <input
                          value={opt}
                          onChange={(e) => updateOption(q, optIndex, e.target.value)}
                          placeholder={`الاختيار ${optIndex + 1}`}
                          className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                        />
                        <IconButton label="حذف الاختيار" type="button" variant="ghost" size="sm"
                          onClick={() => removeOption(q, optIndex)} disabled={q.options.length <= 2}>
                          <Trash2 className="h-4 w-4 text-danger-500" />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}

                {q.saveError && <p className="text-xs text-danger-600 font-medium">{q.saveError}</p>}
                {mode === 'edit' && (
                  <Button type="button" variant="secondary" size="sm"
                    loading={savingQ === q.localId}
                    onClick={() => { setSavingQ(q.localId); persistQuestion(q, index).finally(() => setSavingQ(null)); }}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> حفظ السؤال
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}